import React from "react";
import { useNavigate } from "react-router-dom";
import { LuCamera, LuClipboardList, LuCreditCard, LuDownload, LuHouse, LuMail, LuMessageCircle, LuPackage, LuUser } from "react-icons/lu";
import { PageLayout } from "../../app/PageLayout";
import { Button, Card, Field, Input, RadioCards, SaveStatus, Spinner, cn } from "../../components/ui";
import { usePatientForm } from "../../hooks/usePatientForm";
import { usePatientOcr } from "./usePatientOcr";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { useAccess } from "../../hooks/useAccess";
import { usePdfActions } from "../../hooks/usePdfActions";
import { useAutoSave } from "../../hooks/useAutoSave";
import { formatCpf } from "../../lib/formatters";
import { MAX_UPLOAD_BYTES, birthDateError, cardValidityError, cpfError } from "../../lib/validators";
import { showError } from "../../lib/feedback";
import { ShareCard } from "../../components/patterns/ShareCard";
import { INTERNACAO_PLANS, OPME_PLANS } from "../../constants/healthPlans";
import { HealthPlanPicker, SummaryDescriptionDialog } from "../guides";
import { EmailComposer, WhatsAppComposer } from "../sharing";
import { t } from "../../i18n";
import { PATHS } from "../../app/paths";

const today = () => new Date().toISOString().slice(0, 10);

export const PatientDataPage: React.FC = () => {
  const context = useOrderDraft();
  const { canUseOcr } = useAccess();
  const patient = usePatientForm();
  const ocr = usePatientOcr();
  const pdfActions = usePdfActions();
  const navigate = useNavigate();
  const [showSendEmailModal, setShowSendEmailModal] = React.useState(false);
  const [showSendWhatsAppModal, setShowSendWhatsAppModal] = React.useState(false);
  const patientName = context.surgeryRequestData.patientName;
  // O erro só aparece depois que a pessoa sai do campo, não enquanto digita.
  const [touched, setTouched] = React.useState({ cpf: false, birth: false, card: false });
  const touch = (field: keyof typeof touched) => () => setTouched((prev) => ({ ...prev, [field]: true }));
  // CPF incompleto ou inválido não vai para o backend; o /chat/save mescla os campos, então o último válido fica salvo.
  const savedCpf = cpfError(patient.cpf) ? undefined : patient.cpf;
  const cpfRef = React.useRef<HTMLInputElement>(null);
  const birthRef = React.useRef<HTMLInputElement>(null);

  // Campo vazio não trava (a guia sai sem ele), mas CPF ou nascimento inválido não vai para o PDF.
  const handleDownloadGuide = () => {
    const invalid = cpfError(patient.cpf) ? cpfRef : birthDateError(patient.birthDate) ? birthRef : null;
    if (invalid) {
      setTouched({ cpf: true, birth: true, card: true });
      invalid.current?.focus();
      return;
    }
    pdfActions.openGuiaInternacaoFlow();
  };

  const save = useAutoSave(
    {
      timestamp: context.timestamp,
      cpf: savedCpf,
      sexo: patient.sexo,
      numCarteirinha: patient.documentNumber,
      convenio: patient.convenio,
      valCarteirinha: patient.documentValidity,
      dataNasc: patient.birthDate,
    },
    [savedCpf, patient.sexo, patient.documentNumber, patient.convenio, patient.documentValidity, patient.birthDate]
  );

  const handleClickToHome = () => {
    context.resetDraft();
    navigate(PATHS.newOrder);
  };

  const handleCardPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file && file.size > MAX_UPLOAD_BYTES) return showError("file-too-large");
    if (file &&(await ocr.upload(file, file.name)) !== null) await ocr.analyze(file.name);
  };

  return (
    <>
      <PageLayout
        title={t("patientData.title")}
        subtitle={<SaveStatus status={save.status} savedAt={save.savedAt} />}
        onBack={() => navigate(PATHS.orderReview)}
        nav={false}
        action={
          <Button variant="outline" size="sm" icon={<LuHouse size={18} aria-hidden />} onClick={handleClickToHome} aria-label={t("patientData.home")}>
            <span className="hidden min-[381px]:inline">{t("patientData.home")}</span>
          </Button>
        }
        cta={
          <Button variant="primary" size="lg" icon={<LuDownload size={20} aria-hidden />} onClick={handleDownloadGuide}>
            {t("patientData.downloadGuide")}
          </Button>
        }
      >
        {canUseOcr && (
          <label
            className={cn(
              "flex cursor-pointer items-center gap-3.5 rounded-card border-strong border-dashed border-primary bg-primary-tint p-4 focus-within:shadow-focus",
              ocr.loading && "pointer-events-none opacity-70"
            )}
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary text-on-primary" aria-hidden>
              {ocr.loading ? <Spinner /> : <LuCamera size={22} />}
            </span>
            <span className="min-w-0 flex-1">
              <strong className="block font-semibold">{t("patientData.ocr.title")}</strong>
              <span className="block text-caption text-fg-subtle">{t("patientData.ocr.description")}</span>
            </span>
            <input type="file" accept="image/*,application/pdf" capture="environment" className="sr-only" onChange={handleCardPhoto} disabled={ocr.loading} />
          </label>
        )}

        <Card title={t("patientData.identification")} icon={<LuUser size={20} />}>
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <Field label={t("patientData.name")}>
              <Input
                value={patientName}
                onChange={(e) => context.setSurgeryRequestData((prev) => ({ ...prev, patientName: e.target.value }))}
                autoComplete="off"
              />
            </Field>
            <Field label={t("patientData.birth")} error={touched.birth ? birthDateError(patient.birthDate) : null}>
              <Input ref={birthRef} type="date" min="1900-01-01" max={today()} value={patient.birthDate} onChange={(e) => patient.setBirthDate(e.target.value)} onBlur={touch("birth")} />
            </Field>
            <Field label={t("patientData.cpf")} error={touched.cpf ? cpfError(patient.cpf) : null}>
              <Input
                ref={cpfRef}
                value={patient.cpf}
                onChange={(e) => patient.setCpf(formatCpf(e.target.value))}
                onBlur={touch("cpf")}
                placeholder="000.000.000-00"
                maxLength={14}
                inputMode="numeric"
              />
            </Field>
            <RadioCards
              label={t("patientData.sex")}
              value={patient.sexo}
              onChange={patient.setSexo}
              options={[
                { value: "F", title: t("patientData.female") },
                { value: "M", title: t("patientData.male") },
              ]}
            />
          </div>
        </Card>

        <Card title={t("patientData.healthPlan")} icon={<LuCreditCard size={20} />}>
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <Field label={t("patientData.planProduct")}>
              <Input value={patient.convenio} onChange={(e) => patient.setConvenio(e.target.value)} placeholder={t("patientData.planProductPlaceholder")} />
            </Field>
            <Field label={t("patientData.cardNumber")}>
              <Input value={patient.documentNumber} onChange={(e) => patient.setDocumentNumber(e.target.value)} placeholder={t("patientData.cardNumberPlaceholder")} />
            </Field>
            <Field label={t("patientData.cardValidity")} error={touched.card ? cardValidityError(patient.documentValidity) : null}>
              <Input type="month" value={patient.documentValidity} onChange={(e) => patient.setDocumentValidity(e.target.value)} onBlur={touch("card")} />
            </Field>
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-2.5">
          <Button
            variant="outline"
            icon={<LuClipboardList size={18} aria-hidden />}
            loading={pdfActions.downloadResumo.loading}
            onClick={() => pdfActions.handleFinalDownloadResumo(context.lastApprovedRefs?.length ? context.lastApprovedRefs : undefined)}
          >
            {t("patientData.summary")}
          </Button>
          <Button variant="outline" icon={<LuPackage size={18} aria-hidden />} onClick={() => pdfActions.setShowOpmeSelection(true)}>
            {t("patientData.opme")}
          </Button>
        </div>

        <h2 className="text-heading font-semibold text-fg-secondary">{t("patientData.share")}</h2>
        <div className="grid gap-2.5 sm:grid-cols-2">
          <ShareCard
            tone="mail"
            icon={<LuMail size={20} />}
            title={t("patientData.email")}
            description={t("patientData.emailDescription")}
            onClick={() => setShowSendEmailModal(true)}
          />
          <ShareCard
            tone="whatsapp"
            icon={<LuMessageCircle size={20} />}
            title={t("patientData.whatsapp")}
            description={t("patientData.whatsappDescription")}
            onClick={() => setShowSendWhatsAppModal(true)}
          />
        </div>
      </PageLayout>

      {showSendEmailModal && (
        <EmailComposer
          onClose={() => setShowSendEmailModal(false)}
          patientName={patientName}
        />
      )}

      {showSendWhatsAppModal && <WhatsAppComposer onClose={() => setShowSendWhatsAppModal(false)} patientName={patientName} />}

      {pdfActions.showDescricaoResumidaModal && (
        <SummaryDescriptionDialog
          suggestion={pdfActions.resumidaSuggestion}
          loading={pdfActions.generatingResumida}
          error={pdfActions.resumidaError}
          onRegenerate={pdfActions.generateDescricaoResumida}
          onConfirm={pdfActions.confirmDescricaoResumida}
          onClose={pdfActions.closeGuiaInternacaoFlow}
        />
      )}

      {pdfActions.showPlanSelection && (
        <HealthPlanPicker
          title={t("guides.admissionTitle")}
          plans={INTERNACAO_PLANS}
          loading={pdfActions.downloadInternacao.loading}
          onClose={() => pdfActions.setShowPlanSelection(false)}
          onDownload={pdfActions.handleDownloadGuiaInternacao}
          reportType="_pedido_de_cirurgia_completo"
          secondStep
        />
      )}
      {pdfActions.showOpmeSelection && (
        <HealthPlanPicker
          title={t("guides.opmeTitle")}
          plans={OPME_PLANS}
          loading={pdfActions.downloadOpme.loading}
          onClose={() => pdfActions.setShowOpmeSelection(false)}
          onDownload={pdfActions.handleDownloadOPME}
          reportType="_opme_completo"
        />
      )}
    </>
  );
};
